import { z } from 'zod';

import { BusinessOrganisationSchema } from './businessOrganisationSchema.ts';

export const LiteralStaticConfigSchema = z.string().min(1);

export const IdNameStaticConfigSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const NameValueStaticConfigSchema = z.object({
  name: LiteralStaticConfigSchema,
  value: LiteralStaticConfigSchema,
});

export type IdNameStaticConfigModel = z.infer<typeof IdNameStaticConfigSchema>;
export const JobStatusOptionsSchema = z.object({
  jobStatuses: z.string().array(),
  reasonForChange: z.string().array(),
});

export const jobTypes = z.object({
  id: z.uuid(),
  name: z.string(),
  value: z.number(),
});

export const BaseUtcOffsetSchema = z.object({
  ticks: z.number().optional(),
  days: z.number().optional(),
  hours: z.number().optional(),
  milliseconds: z.number().optional(),
  minutes: z.number().optional(),
  seconds: z.number().optional(),
  totalDays: z.number().optional(),
  totalHours: z.number().optional(),
  totalMilliseconds: z.number().optional(),
  totalMinutes: z.number().optional(),
  totalSeconds: z.number().optional(),
});

export const TimeZoneSchema = z.object({
  name: z.string(),
  baseUtcOffset: BaseUtcOffsetSchema.or(z.string()).optional(),
  displayName: z.string(),
  zoneId: z.string(),
});

// None needs removing in Sprint 6 after existing service locations have been updated
export const ServiceTypesSchema = z.enum(['Mobile', 'Static', 'None']);

export const FulfilmentStatusReasonCodeSchema = z.object({
  id: z.uuid(),
  reason: LiteralStaticConfigSchema,
});

export const OrderJobItemPartFulfilmentStatusSchema = z.object({
  id: z.uuid(),
  status: LiteralStaticConfigSchema,
  reasonCodes: FulfilmentStatusReasonCodeSchema.array(),
});

export const CountrySchema = z.object({
  name: LiteralStaticConfigSchema,
  code: LiteralStaticConfigSchema,
});

export const ServiceJobTypeSchema = z.object({
  jobTypeId: LiteralStaticConfigSchema,
  name: LiteralStaticConfigSchema,
});
export const QcRatingServiceJobTypeSchema = z.object({
  id: LiteralStaticConfigSchema,
  name: LiteralStaticConfigSchema,
  jobType: ServiceJobTypeSchema,
});

export const OrderStatusOptionsSchema = z.object({
  orderStatuses: z.string().array(),
  reasonForChange: z.string().array(),
});

export const OrderStatusOptionsEnum = z.enum([
  'customerRequested',
  'ordernolongerrequired',
  'ordercreatedinerror',
  'other',
]); // Need enum conversion to handle this correctly. Just a temp fix to satisfy translation

export const FleetEnabledSchema = z.object({
  enabled: z.boolean(),
});

export const StockEnabledForOrdersSchema = z.enum(['true', 'false']);

export const CustomerFeatureFlagsSchema = z.object({
  usesCustomerAffiliationProgram: z.boolean(),
  useTaxExemption: z.boolean(),
  useEmployeeId: z.boolean(),
  // TODO(BE): Store Accounting sub-nav flags — BE to add these two to the same feature-flags
  // response. Optional until BE ships them; FE treats missing as false.
  // endOfDayEnabled: show BSRO / hide MyCar; cashAndBankingEnabled: hide BSRO / show MyCar.
  endOfDayEnabled: z.boolean().optional(),
  cashAndBankingEnabled: z.boolean().optional(),
});

export const FeatureConfigSchema = z.object({
  name: z.string(),
  isEnabled: z.boolean(),
});

export const PurchaseOrderNumberFormatSchema = z.object({
  values: z.string().array(),
});

export const CustomerValidAffiliationNamesSchema = z.object({
  values: z.string().array(),
});

export const LocaleSchema = z.object({
  value: z.enum(['en-US', 'en-GB', 'en-AU']),
});

export const StaticConfigDataSchema = z.object({
  businessOrganisation: BusinessOrganisationSchema.nullish(),
  countryOptions: CountrySchema.array(),
  fulfilmentTypes: IdNameStaticConfigSchema.array(),
  jobStatusOptions: JobStatusOptionsSchema,
  jobTypeOptions: IdNameStaticConfigSchema.array(),
  locationTypes: LiteralStaticConfigSchema.array(),
  orderStatusOptions: OrderStatusOptionsSchema,
  productGroups: IdNameStaticConfigSchema.array(),
  qcRatingServiceJobTypeOptions: QcRatingServiceJobTypeSchema.array(),
  stockTakeProfileCycleDayOptions: NameValueStaticConfigSchema.array(),
  timeZones: TimeZoneSchema.array(),
  fleetOptions: FleetEnabledSchema,
  stockEnabledForOrdersOptions: StockEnabledForOrdersSchema,
  oneSourceEnabled: FeatureConfigSchema,
  purchaseOrderDraftEnabled: FeatureConfigSchema,
  callOffPurchaseOrdersEnabled: FeatureConfigSchema,
  oneTimeSupplierEnabled: FeatureConfigSchema,
  purchaseOrderNumberFormats: PurchaseOrderNumberFormatSchema,
  validAffiliationNames: CustomerValidAffiliationNamesSchema,
  stockReturnReasons: z.string().array(),
  locale: LocaleSchema,
  customerFeatureFlags: CustomerFeatureFlagsSchema,
});
export const StaticConfigDataKeySchema = StaticConfigDataSchema.keyof();

/// TYPES...

export type NameValueStaticConfigModel = z.infer<
  typeof NameValueStaticConfigSchema
>;
export type StaticConfigDataModel = z.infer<typeof StaticConfigDataSchema>;

export type TimeZoneModel = z.infer<typeof TimeZoneSchema>;

export type ServiceTypesModel = z.infer<typeof ServiceTypesSchema>;

export type CountryModel = z.infer<typeof CountrySchema>;

export type OrderStatusOptionsModel = z.infer<typeof OrderStatusOptionsSchema>;
export type JobStatusOptionsModel = z.infer<typeof JobStatusOptionsSchema>;
export type ProductGroupModel = z.infer<typeof IdNameStaticConfigSchema>;
export type JobTypeModel = z.infer<typeof IdNameStaticConfigSchema>;

export type FulfilmentTypeModel = z.infer<typeof IdNameStaticConfigSchema>;

export type QcRatingServiceJobTypeModel = z.infer<
  typeof QcRatingServiceJobTypeSchema
>;

export type FleetEnabledModel = z.infer<typeof FleetEnabledSchema>;
export type StockEnabledForOrdersModel = z.infer<
  typeof StockEnabledForOrdersSchema
>;
export type CustomerFeatureFlagsModel = z.infer<
  typeof CustomerFeatureFlagsSchema
>;

export type FeatureConfigModel = z.infer<typeof FeatureConfigSchema>;

export type PurchaseOrderNumberFormatType = z.infer<
  typeof PurchaseOrderNumberFormatSchema
>;

export type CustomerValidAffiliationNamesType = z.infer<
  typeof CustomerValidAffiliationNamesSchema
>;

export type LocaleType = z.infer<typeof LocaleSchema>;
